import { DestroyRef, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, map, Observable, of } from 'rxjs';
import { PermissionClient, PermissionResponse } from '../../../core/openapi/identity';
import { IPermission, IPermissionGroup } from '../roles.model';
import { LoadingState } from '../../../shared/types/loading.type';
import { ToastService } from '@ui-lib/components';
import { TranslateService } from '@ngx-translate/core';
import { outputError } from '../../../shared/utils/output-error.function';

@Injectable()
export class RolePermissionsService {
  private readonly permissionClient = inject(PermissionClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  private readonly _loadingState: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );

  public readonly loadingState: Signal<LoadingState> = this._loadingState.asReadonly();

  public loadPermissions(): Observable<IPermissionGroup[]> {
    this._loadingState.set(LoadingState.LOADING);

    return this.permissionClient.permissionGet().pipe(
      map((response): IPermissionGroup[] =>
        response.map(
          (group): IPermissionGroup => ({
            ...group,
            hidden: false,
            selected: false,
            permissions: group.permissions.map(
              (item: PermissionResponse): IPermission => ({
                ...item,
                selected: false,
              }),
            ),
          }),
        ),
      ),
      catchError((err) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.roles.toast.permission-fetching-failed',
            {
              error: outputError(err, this.translateService.instant('errors.unknown')),
            },
          ),
        });

        return of([]);
      }),
      finalize(() => this._loadingState.set(LoadingState.READY)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public selectRolePermissions(
    permissionIds: string[],
    groups: IPermissionGroup[],
  ): IPermissionGroup[] {
    const permissionIdSet = new Set(permissionIds);
    return groups.map((group): IPermissionGroup => {
      const permissions = group.permissions.map(
        (permission): IPermission => ({
          ...permission,
          selected: permissionIdSet.has(permission.id),
        }),
      );
      return {
        ...group,
        permissions,
        selected: permissions.every((p) => p.selected),
      };
    });
  }

  public togglePermissionGroup(
    groups: IPermissionGroup[],
    group: IPermissionGroup,
  ): IPermissionGroup[] {
    return groups.map((g): IPermissionGroup => {
      if (g.id !== group.id) return g;
      const selected = !g.selected;
      return {
        ...g,
        selected,
        permissions: g.permissions.map((p): IPermission => ({ ...p, selected })),
      };
    });
  }

  public togglePermission(
    groups: IPermissionGroup[],
    permissionGroup: IPermissionGroup,
    permission: IPermission,
  ): IPermissionGroup[] {
    return groups.map((g): IPermissionGroup => {
      if (g.id !== permissionGroup.id) return g;
      const permissions = g.permissions.map(
        (p): IPermission => (p.id === permission.id ? { ...p, selected: !p.selected } : p),
      );
      return {
        ...g,
        permissions,
        selected: permissions.every((p) => p.selected),
      };
    });
  }

  public getSelectedPermissionIds(groups: IPermissionGroup[]): string[] {
    return groups.flatMap((group) =>
      group.permissions.filter((item) => item.selected).map((item) => item.id),
    );
  }
}
